import { supabase } from './supabase';
import { slugify } from './slug';
import { boardService } from './boardService';
import { fileManagerPreloader } from '@/components/file-manager/utils/fileManagerPreloader';

export interface ProjectStage {
  id: string;
  name: string;
  is_completed: boolean;
  start_date?: string;
  end_date?: string;
}

export interface Project {
  id: string;
  workspace_id: string;
  name: string;
  slug: string;
  description: string;
  cover_image: string | null;
  status: 'active' | 'completed' | 'on_hold' | 'cancelled';
  start_date: string | null;
  end_date: string | null;
  progress: number;
  brand_id: string | null;
  task_board_id: string | null;
  folder_id: string | null;
  channel_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  stages?: ProjectStage[];
  rate?: number;
  rate_type?: 'fixed' | 'hourly';
  // Joined data
  members?: ProjectMember[];
  brand?: { id: string; name: string; logo_url: string | null } | null;
}

export interface ProjectMember {
  id: string;
  project_id: string;
  user_id: string;
  role: 'owner' | 'manager' | 'client';
  permission: 'edit' | 'view';
  added_at: string;
  // Joined profile data
  profile?: {
    first_name: string | null;
    last_name: string | null;
    email: string;
    avatar_url: string | null;
  };
}

export interface CreateProjectData {
  workspaceId: string;
  userId: string;
  name: string;
  description?: string;
  coverImage?: string;
  startDate?: string;
  endDate?: string;
  brandId?: string;
  clientIds?: { userId: string; permission: 'edit' | 'view' }[];
  companyIds?: string[];
  teamMemberIds?: string[];
  stages?: { name: string; start_date?: string; end_date?: string }[];
  rate?: number;
  rateType?: 'fixed' | 'hourly';
}

const projectsCache = new Map<string, Project[]>();

export const projectService = {
  clearCache() {
    projectsCache.clear();
  },

  async getProjectsByWorkspace(workspaceId: string): Promise<Project[]> {
    const { data, error } = await supabase
      .from('projects')
      .select(`
        *,
        project_members (
          id, project_id, user_id, role, permission, added_at
        )
      `)
      .eq('workspace_id', workspaceId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Fetch profiles for all members
    const allUserIds = new Set<string>();
    (data || []).forEach(p => {
      p.project_members?.forEach((m: any) => allUserIds.add(m.user_id));
    });

    let profilesMap = new Map<string, any>();
    if (allUserIds.size > 0) {
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email, avatar_url')
        .in('id', Array.from(allUserIds));

      profilesMap = new Map((profiles || []).map(p => [p.id, p]));
    }

    return (data || []).map(p => ({
      ...p,
      members: (p.project_members || []).map((m: any) => ({
        ...m,
        profile: profilesMap.get(m.user_id) || null,
      })),
    }));
  },

  async getProjectsForClient(workspaceId: string, userId: string): Promise<Project[]> {
    const { data, error } = await supabase
      .from('projects')
      .select(`
        *,
        project_members!inner (
          id, project_id, user_id, role, permission, added_at
        )
      `)
      .eq('workspace_id', workspaceId)
      .eq('project_members.user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async getProjectBySlug(workspaceId: string, slug: string): Promise<Project | null> {
    const { data, error } = await supabase
      .from('projects')
      .select(`
        *,
        project_members (
          id, project_id, user_id, role, permission, added_at
        ),
        brands:brand_id (
          id, name, logo_url
        )
      `)
      .eq('workspace_id', workspaceId)
      .eq('slug', slug)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;

    // Fetch profiles for members
    const userIds = (data.project_members || []).map((m: any) => m.user_id);
    let profilesMap = new Map<string, any>();
    if (userIds.length > 0) {
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email, avatar_url')
        .in('id', userIds);
      profilesMap = new Map((profiles || []).map(p => [p.id, p]));
    }

    return {
      ...data,
      brand: data.brands || null,
      members: (data.project_members || []).map((m: any) => ({
        ...m,
        profile: profilesMap.get(m.user_id) || null,
      })),
    };
  },

  async generateUniqueSlug(workspaceId: string, name: string): Promise<string> {
    const baseSlug = slugify(name);
    const { data } = await supabase
      .from('projects')
      .select('slug')
      .eq('workspace_id', workspaceId)
      .eq('slug', baseSlug)
      .maybeSingle();

    if (!data) return baseSlug;

    let counter = 1;
    let candidate = `${baseSlug}-${counter}`;
    while (true) {
      const { data: existing } = await supabase
        .from('projects')
        .select('slug')
        .eq('workspace_id', workspaceId)
        .eq('slug', candidate)
        .maybeSingle();
      if (!existing) return candidate;
      counter++;
      candidate = `${baseSlug}-${counter}`;
    }
  },

  async createProject(data: CreateProjectData): Promise<Project> {
    const slug = await this.generateUniqueSlug(data.workspaceId, data.name);

    // 1. Create the task board
    const board = await boardService.createBoard(
      data.workspaceId,
      data.userId,
      data.name,
      `Task board for project: ${data.name}`,
      { type: 'gradient', value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }
    );

    // 2. Create private chat channel
    let channelId: string | null = null;
    try {
      const { data: channel, error: channelError } = await supabase
        .from('chat_channels')
        .insert({
          workspace_id: data.workspaceId,
          name: data.name,
          description: `Project channel for ${data.name}`,
          type: 'private',
          created_by: data.userId,
        })
        .select()
        .single();

      if (!channelError && channel) {
        channelId = channel.id;

        // Add creator as channel owner
        const channelMembers: any[] = [
          { channel_id: channel.id, user_id: data.userId, role: 'owner' },
        ];

        // Add team members to channel
        if (data.teamMemberIds) {
          data.teamMemberIds.forEach(id => {
            if (id !== data.userId) {
              channelMembers.push({ channel_id: channel.id, user_id: id, role: 'member' });
            }
          });
        }

        // Add clients to channel
        if (data.clientIds) {
          data.clientIds.forEach(c => {
            channelMembers.push({ channel_id: channel.id, user_id: c.userId, role: 'member' });
          });
        }

        await supabase.from('chat_channel_members').insert(channelMembers);
      }
    } catch (err) {
      console.error('Error creating project channel:', err);
    }

    // 3. Create the project record
    const { data: project, error } = await supabase
      .from('projects')
      .insert({
        workspace_id: data.workspaceId,
        name: data.name,
        slug,
        description: data.description || '',
        cover_image: data.coverImage || null,
        start_date: data.startDate || null,
        end_date: data.endDate || null,
        brand_id: data.brandId || null,
        task_board_id: board.id,
        folder_id: null,
        channel_id: channelId,
        created_by: data.userId,
        rate: data.rate || 0,
        rate_type: data.rateType || 'fixed',
        stages: data.stages ? data.stages.map(name => ({ 
          id: Math.random().toString(36).substring(2, 11), 
          name, 
          is_completed: false 
        })) : [],
      })
      .select()
      .single();

    if (error) throw error;

    // 4. Create the project root folder in workspace_files under the default "Projects" folder
    try {
      let projectsParentId: string | null = null;
      const { data: existingProjectsFolder } = await supabase
        .from('workspace_files')
        .select('id')
        .eq('workspace_id', data.workspaceId)
        .eq('name', 'Projects')
        .eq('type', 'folder')
        .is('parent_id', null)
        .eq('trashed', false)
        .maybeSingle();

      if (existingProjectsFolder) {
        projectsParentId = existingProjectsFolder.id;
      } else {
        const { data: newProjectsFolder } = await supabase
          .from('workspace_files')
          .insert({
            workspace_id: data.workspaceId,
            name: 'Projects',
            type: 'folder',
            parent_id: null,
            size: 0,
            starred: false,
            shared: false,
            trashed: false,
            uploaded_by: data.userId,
          })
          .select('id')
          .single();
        if (newProjectsFolder) {
          projectsParentId = newProjectsFolder.id;
        }
      }

      const { data: folder, error: folderError } = await supabase
        .from('workspace_files')
        .insert({
          workspace_id: data.workspaceId,
          project_id: project.id,
          name: data.name,
          type: 'folder',
          parent_id: projectsParentId,
          size: 0,
          starred: false,
          shared: false,
          trashed: false,
          uploaded_by: data.userId,
        })
        .select()
        .single();

      if (!folderError && folder) {
        await supabase
          .from('projects')
          .update({ folder_id: folder.id })
          .eq('id', project.id);
        project.folder_id = folder.id;
      }
    } catch (err) {
      console.error('Error creating project folder:', err);
    }

    // 5. Add project members
    const projectMembers: any[] = [
      { project_id: project.id, user_id: data.userId, role: 'owner', permission: 'edit' },
    ];

    if (data.teamMemberIds) {
      data.teamMemberIds.forEach(id => {
        if (id !== data.userId) {
          projectMembers.push({ project_id: project.id, user_id: id, role: 'manager', permission: 'edit' });
        }
      });
    }

    const clientUsersToAdd = new Map<string, { userId: string; permission: 'edit' | 'view' }>();

    if (data.clientIds) {
      data.clientIds.forEach(c => {
        clientUsersToAdd.set(c.userId, c);
      });
    }

    if (data.companyIds && data.companyIds.length > 0) {
      // Fetch all clients linked to these companies
      const { data: companyClients } = await supabase
        .from('workspace_members')
        .select('user_id')
        .in('company_id', data.companyIds)
        .eq('role', 'client')
        .eq('status', 'active');
      
      if (companyClients) {
        companyClients.forEach(c => {
          if (!clientUsersToAdd.has(c.user_id)) {
            clientUsersToAdd.set(c.user_id, { userId: c.user_id, permission: 'view' });
          }
        });
      }
    }

    clientUsersToAdd.forEach(c => {
      projectMembers.push({ project_id: project.id, user_id: c.userId, role: 'client', permission: c.permission });
    });

    await supabase.from('project_members').insert(projectMembers);

    projectsCache.delete(data.workspaceId);
    fileManagerPreloader.invalidate(data.workspaceId);
    return project;
  },

  async updateProject(projectId: string, updates: Partial<Pick<Project, 'name' | 'description' | 'cover_image' | 'status' | 'start_date' | 'end_date' | 'progress' | 'brand_id' | 'stages' | 'rate' | 'rate_type'>>): Promise<Project> {
    const { data, error } = await supabase
      .from('projects')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', projectId)
      .select()
      .single();

    if (error) throw error;
    projectsCache.delete(data.workspace_id);
    return data;
  },

  async deleteProject(projectId: string): Promise<void> {
    // The database trigger handles cascade deletion of linked resources
    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', projectId);

    if (error) throw error;
    projectsCache.clear();
  },

  async addProjectMember(projectId: string, userId: string, role: 'manager' | 'client', permission: 'edit' | 'view'): Promise<void> {
    const { error } = await supabase
      .from('project_members')
      .insert({ project_id: projectId, user_id: userId, role, permission });

    if (error) throw error;
  },

  async updateProjectMemberPermission(projectId: string, userId: string, permission: 'edit' | 'view'): Promise<void> {
    const { error } = await supabase
      .from('project_members')
      .update({ permission })
      .eq('project_id', projectId)
      .eq('user_id', userId);

    if (error) throw error;
  },

  async removeProjectMember(projectId: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('project_members')
      .delete()
      .eq('project_id', projectId)
      .eq('user_id', userId);

    if (error) throw error;
  },

  async inviteClient(
    workspaceId: string, 
    email: string, 
    data: {
      firstName?: string;
      lastName?: string;
      phone?: string;
      website?: string;
      companyName?: string;
      category?: string;
      agentId?: string;
      country?: string;
      state?: string;
      city?: string;
      zip?: string;
      streetAddress?: string;
      avatarUrl?: string;
      companyId?: string;
      sendEmail?: boolean;
      designation?: string;
      emailType?: string;
      phoneType?: string;
      socialMedia?: any;
      additionalEmails?: any[];
      additionalPhones?: any[];
      source?: string;
    },
    projectId?: string, 
    permission: 'edit' | 'view' = 'view'
  ): Promise<void> {
    const { 
      firstName = '', 
      lastName = '', 
      phone = '', 
      website = '', 
      companyName = '', 
      category = '', 
      agentId, 
      country = '', 
      state = '', 
      city = '', 
      zip = '', 
      streetAddress = '', 
      avatarUrl = '',
      companyId,
      sendEmail = true,
      designation,
      emailType,
      phoneType,
      socialMedia,
      additionalEmails,
      additionalPhones,
      source
    } = data;

    // 1. Optionally send magic link invitation via Supabase Auth
    if (sendEmail) {
      const { error: authError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: true,
          data: {
            workspace_id: workspaceId,
            role: 'client',
            first_name: firstName,
            last_name: lastName,
            phone: phone,
            full_name: `${firstName} ${lastName}`.trim(),
            avatar_url: avatarUrl
          },
        },
      });

      if (authError) throw authError;
    }

    // 2. Check if the user already exists in profiles
    const { data: profile } = await supabase
      .from('profiles')
      .select('id')
      .eq('email', email)
      .maybeSingle();

    if (profile) {
      // User exists, update profile with provided details if they were provided
      const profileUpdates: any = {};
      if (firstName) profileUpdates.first_name = firstName;
      if (lastName) profileUpdates.last_name = lastName;
      if (phone) profileUpdates.phone = phone;
      if (avatarUrl) profileUpdates.avatar_url = avatarUrl;
      if (website) profileUpdates.website = website;
      if (companyName) profileUpdates.company_name = companyName;
      if (country) profileUpdates.country = country;
      if (state) profileUpdates.state = state;
      if (city) profileUpdates.city = city;
      if (zip) profileUpdates.zip = zip;
      if (streetAddress) profileUpdates.street_address = streetAddress;
      if (designation) profileUpdates.designation = designation;
      if (emailType) profileUpdates.email_type = emailType;
      if (phoneType) profileUpdates.phone_type = phoneType;
      if (socialMedia) profileUpdates.social_media = socialMedia;
      if (additionalEmails) profileUpdates.additional_emails = additionalEmails;
      if (additionalPhones) profileUpdates.additional_phones = additionalPhones;
      if (source) profileUpdates.source = source;

      if (Object.keys(profileUpdates).length > 0) {
        await supabase
          .from('profiles')
          .update(profileUpdates)
          .eq('id', profile.id);
      }

      // Check if already a member
      const { data: existingMember } = await supabase
        .from('workspace_members')
        .select('id')
        .eq('workspace_id', workspaceId)
        .eq('user_id', profile.id)
        .maybeSingle();

      const memberData: any = {
        workspace_id: workspaceId,
        user_id: profile.id,
        role: 'client' as const,
        status: 'active' as const,
        website,
        company_name: companyName,
        category,
        agent_id: agentId,
        country,
        state,
        city,
        zip,
        street_address: streetAddress,
        designation,
        email_type: emailType,
        phone_type: phoneType,
        social_media: socialMedia,
        additional_emails: additionalEmails,
        additional_phones: additionalPhones,
        source
      };

      if (companyId) {
        memberData.company_id = companyId;
      }

      if (!existingMember) {
        // Add them directly to workspace_members
        await supabase
          .from('workspace_members')
          .insert(memberData);
      } else {
        // Update existing member record with new metadata
        await supabase
          .from('workspace_members')
          .update(memberData)
          .eq('id', existingMember.id);
      }
        
      // If a project was specified, add them there too
      if (projectId) {
        await supabase
          .from('project_members')
          .upsert({
            project_id: projectId,
            user_id: profile.id,
            role: 'client',
            permission
          }, { onConflict: 'project_id,user_id' });
      }
    } else {
      // User does not exist yet. 
      // Create/Update invitation record
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      const invData: any = {
        workspace_id: workspaceId,
        email,
        role: 'client',
        invited_by: currentUser?.id,
        first_name: firstName,
        last_name: lastName,
        phone: phone,
        website,
        company_name: companyName,
        category,
        agent_id: agentId,
        country,
        state,
        city,
        zip,
        street_address: streetAddress,
        avatar_url: avatarUrl,
        designation,
        email_type: emailType,
        phone_type: phoneType,
        social_media: socialMedia,
        additional_emails: additionalEmails,
        additional_phones: additionalPhones,
        source
      };
      
      if (companyId) {
        invData.company_id = companyId;
      }

      await supabase.from('workspace_invitations').upsert(invData, { onConflict: 'workspace_id,email' });
    }

    // Invalidate pipeline cache because client details affect mapped deals
    const { pipelineService } = await import('./pipelineService');
    pipelineService.clearCache();
  },

  async sendMagicLink(workspaceId: string, email: string, firstName?: string, lastName?: string): Promise<void> {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: true,
        data: {
          workspace_id: workspaceId,
          role: 'client',
          first_name: firstName,
          last_name: lastName,
          full_name: firstName && lastName ? `${firstName} ${lastName}`.trim() : undefined
        },
      },
    });
    if (error) throw error;
  },

  async deleteClient(workspaceId: string, userId: string): Promise<void> {
    // Remove from workspace_members
    const { error } = await supabase
      .from('workspace_members')
      .delete()
      .eq('workspace_id', workspaceId)
      .eq('user_id', userId);

    if (error) throw error;
  },

  async getWorkspaceClients(workspaceId: string): Promise<any[]> {
    // 1. Get active members
    const { data: members, error: membersError } = await supabase
      .from('workspace_members')
      .select('id, user_id, role, status, joined_at, category, agent_id, company_name, website, country, state, city, zip, street_address, first_name, last_name')
      .eq('workspace_id', workspaceId)
      .eq('role', 'client')
      .eq('status', 'active');

    if (membersError) throw membersError;

    let invitations: any[] | null = [];
    try {
      const { data, error: inviteError } = await supabase
        .from('workspace_invitations')
        .select('*')
        .eq('workspace_id', workspaceId)
        .eq('role', 'client');

      if (!inviteError) {
        invitations = data;
      } else {
        console.warn('Failed to fetch workspace invitations:', inviteError);
      }
    } catch (e) {
      console.warn('Failed to fetch workspace invitations:', e);
    }

    const userIds = (members || []).map(m => m.user_id);
    let profilesMap = new Map<string, any>();
    let countMap = new Map<string, number>();

    if (userIds.length > 0) {
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email, avatar_url, last_sign_in_at')
        .in('id', userIds);

      profilesMap = new Map((profiles || []).map(p => [p.id, p]));

      // Get project counts
      const { data: projectCounts } = await supabase
        .from('project_members')
        .select('user_id, project_id')
        .in('user_id', userIds);

      (projectCounts || []).forEach(pc => {
        countMap.set(pc.user_id, (countMap.get(pc.user_id) || 0) + 1);
      });
    }

    const memberClients = (members || []).map(m => {
      const profile = profilesMap.get(m.user_id);
      const mergedFirstName = m.first_name || profile?.first_name || '';
      const mergedLastName = m.last_name || profile?.last_name || '';
      return {
        id: m.user_id, // For members, we use user_id
        name: profile || m.first_name ? `${mergedFirstName} ${mergedLastName}`.trim() || profile?.email?.split('@')[0] : 'Unknown',
        email: profile?.email || '',
        avatar: profile?.avatar_url,
        role: m.role,
        status: m.status,
        joinedAt: m.joined_at,
        projectCount: countMap.get(m.user_id) || 0,
        isInvitation: false,
        companyName: m.company_name,
        country: m.country,
        lastLogin: profile?.last_sign_in_at
      };
    });

    const invitedClients = (invitations || []).map(inv => ({
      id: inv.id, // For invitations, we use invitation id
      email: inv.email,
      name: `${inv.first_name || ''} ${inv.last_name || ''}`.trim() || inv.email.split('@')[0],
      role: inv.role,
      status: 'pending',
      joinedAt: inv.created_at,
      projectCount: 0,
      isInvitation: true,
      companyId: inv.company_id,
      companyName: inv.company_name,
      country: inv.country,
      lastLogin: null
    }));

    // Filter out invitations that already have a member (to avoid duplicates)
    const existingEmails = new Set(memberClients.map(c => c.email.toLowerCase()));
    const uniqueInvitations = invitedClients.filter(inv => !existingEmails.has(inv.email.toLowerCase()));

    return [...memberClients, ...uniqueInvitations].sort((a, b) => 
      new Date(b.joinedAt).getTime() - new Date(a.joinedAt).getTime()
    );
  },

  // --- Company Methods ---

  async createCompany(workspaceId: string, data: {
    name: string;
    logo_url?: string;
    website?: string;
    email?: string;
    phone?: string;
    category?: string;
    agent_id?: string;
    country?: string;
    state?: string;
    city?: string;
    zip?: string;
    street_address?: string;
    clientIds?: string[];
  }): Promise<any> {
    const { clientIds, ...companyData } = data;
    const { data: { user } } = await supabase.auth.getUser();
    const { data: company, error } = await supabase
      .from('workspace_companies')
      .insert({
        workspace_id: workspaceId,
        created_by: user?.id,
        ...companyData
      })
      .select()
      .single();

    if (error) throw error;

    if (clientIds && clientIds.length > 0) {
      await this.linkClientsToCompany(workspaceId, company.id, clientIds);
    }

    return company;
  },

  async linkClientsToCompany(_workspaceId: string, companyId: string | null, clientUserIds: string[]): Promise<void> {
    if (clientUserIds.length === 0 || !companyId) return;
    
    // Check if there are any existing clients to determine if we should set the first one as owner
    const { count } = await supabase
      .from('workspace_company_clients')
      .select('*', { count: 'exact', head: true })
      .eq('company_id', companyId);

    const isFirstLink = count === 0;
    
    const records = clientUserIds.map((userId, index) => ({
      company_id: companyId,
      user_id: userId,
      role: (isFirstLink && index === 0) ? 'Owner' : 'Member',
      is_owner: (isFirstLink && index === 0)
    }));

    const { error } = await supabase
      .from('workspace_company_clients')
      .upsert(records, { onConflict: 'company_id,user_id' });
      
    if (error) throw error;
  },

  async updateCompanyClientRole(companyId: string, userId: string, role: string): Promise<void> {
    const { error } = await supabase
      .from('workspace_company_clients')
      .update({ role })
      .eq('company_id', companyId)
      .eq('user_id', userId);
    
    if (error) throw error;
  },

  async transferCompanyOwnership(companyId: string, newOwnerId: string): Promise<void> {
    // 1. Remove owner from everyone
    await supabase
      .from('workspace_company_clients')
      .update({ is_owner: false, role: 'Member' })
      .eq('company_id', companyId)
      .eq('is_owner', true);
      
    // 2. Add owner to new owner
    const { error } = await supabase
      .from('workspace_company_clients')
      .update({ is_owner: true, role: 'Owner' })
      .eq('company_id', companyId)
      .eq('user_id', newOwnerId);
      
    if (error) throw error;
  },

  async unlinkClientFromCompany(companyId: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('workspace_company_clients')
      .delete()
      .eq('company_id', companyId)
      .eq('user_id', userId);
      
    if (error) throw error;
  },

  async updateCompany(companyId: string, data: any): Promise<any> {
    const { data: company, error } = await supabase
      .from('workspace_companies')
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq('id', companyId)
      .select()
      .single();

    if (error) throw error;
    return company;
  },

  async deleteCompany(companyId: string): Promise<void> {
    const { error } = await supabase
      .from('workspace_companies')
      .delete()
      .eq('id', companyId);

    if (error) throw error;
  },

  async getWorkspaceCompanies(workspaceId: string): Promise<any[]> {
    const { data: companies, error } = await supabase
      .from('workspace_companies')
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Get linked clients count
    const companyIds = (companies || []).map(c => c.id);
    let countMap = new Map<string, number>();
    
    if (companyIds.length > 0) {
      const { data: links } = await supabase
        .from('workspace_company_clients')
        .select('company_id')
        .in('company_id', companyIds);
        
      if (links) {
        links.forEach(link => {
          if (link.company_id) {
            countMap.set(link.company_id, (countMap.get(link.company_id) || 0) + 1);
          }
        });
      }
    }

    // Get agents (profiles) manually
    const agentIds = (companies || []).map(c => c.agent_id).filter(id => id != null);
    let profilesMap = new Map<string, any>();
    if (agentIds.length > 0) {
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email')
        .in('id', agentIds);
      profilesMap = new Map((profiles || []).map(p => [p.id, p]));
    }

    return (companies || []).map(c => {
      const p = c.agent_id ? profilesMap.get(c.agent_id) : null;
      return {
        ...c,
        clientCount: countMap.get(c.id) || 0,
        agentName: p ? `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.email : null
      };
    });
  },

  async getCompanyClients(companyId: string): Promise<any[]> {
    // 1. Get user IDs and roles from the junction table
    const { data: clientLinks, error: linkError } = await supabase
      .from('workspace_company_clients')
      .select('user_id, role, is_owner')
      .eq('company_id', companyId);

    if (linkError) throw linkError;

    const linkMap = new Map((clientLinks || []).map(link => [link.user_id, link]));
    const userIds = (clientLinks || []).map(link => link.user_id);
    if (userIds.length === 0) return [];

    // 2. Get member data (status, joined_at)
    const { data: members, error } = await supabase
      .from('workspace_members')
      .select('id, user_id, status, joined_at')
      .in('user_id', userIds)
      .eq('role', 'client')
      .eq('status', 'active');

    if (error) throw error;

    const memberUserIds = (members || []).map(m => m.user_id);
    let profilesMap = new Map<string, any>();

    if (memberUserIds.length > 0) {
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email, avatar_url')
        .in('id', memberUserIds);

      profilesMap = new Map((profiles || []).map(p => [p.id, p]));
    }

    return (members || []).map(m => {
      const p = profilesMap.get(m.user_id);
      const link = linkMap.get(m.user_id);
      return {
        id: m.user_id,
        name: p ? `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.email?.split('@')[0] : 'Unknown',
        email: p?.email || '',
        avatar: p?.avatar_url,
        role: link?.role || 'Member',
        isOwner: link?.is_owner || false,
        status: m.status,
        joinedAt: m.joined_at,
      };
    });
  },

  async getCompanyById(companyId: string): Promise<any> {
    const { data, error } = await supabase
      .from('workspace_companies')
      .select('*')
      .eq('id', companyId)
      .single();

    if (error) throw error;
    
    let agentName = null;
    if (data.agent_id) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('first_name, last_name, email')
        .eq('id', data.agent_id)
        .single();
        
      if (profile) {
        agentName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.email;
      }
    }

    return {
      ...data,
      agentName
    };
  }
};


